// Diagnostic reports contain code coordinates, not arbitrary exception messages,
// URLs, customer IDs, browser storage, or request/response bodies.
const sent = new Set();
const kinds = new Set(['Error', 'TypeError', 'ReferenceError', 'RangeError', 'SyntaxError', 'ChunkLoadError']);
const pages = new Set(['booking', 'calendar', 'resources', 'clients', 'sales', 'services', 'products', 'staff', 'settings', 'setting', 'reports', 'login', 'customer-portal', 'consultationforms']);
const bundlePattern = /(?:main|[0-9]+)\.[a-f0-9]{8,64}(?:\.chunk)?\.js/g;

const errorCode = (message) => {
  if (/Cannot read (?:properties|property) of (?:undefined|null)/.test(message)) return 'property_access';
  if (/is not a function/.test(message)) return 'not_callable';
  if (/is not defined/.test(message)) return 'undefined_name';
  if (/Objects are not valid as a React child/.test(message)) return 'invalid_react_child';
  if (/Maximum update depth exceeded|Too many re-renders/.test(message)) return 'render_loop';
  if (/Loading (?:CSS )?chunk|dynamically imported module|module script failed/.test(message)) return 'chunk_load';
  return 'unclassified';
};

const chunkAsset = /^(?:main|[0-9]{1,10})\.[a-f0-9]{8,64}(?:\.chunk)?\.(?:js|css)$/;
const recoveryActions = new Set(['reload_requested', 'guarded', 'recovery_unavailable', 'lazy_content_mounted']);

export const reportFrontendError = ({ reference, error, componentStack = '', chunk }) => {
  try {
    if (!/^EB-[A-Z0-9]{6,16}-[A-Z0-9]{1,8}$/.test(reference) || sent.has(reference) || sent.size >= 5) return;
    const base = process.env.REACT_APP_API_URL;
    if (!base || typeof fetch !== 'function') return;
    const stack = String(error?.stack || '').slice(0, 16000);
    const frames = [...stack.matchAll(/(?:main|[0-9]+)\.[a-f0-9]{8,64}(?:\.chunk)?\.js:\d{1,8}:\d{1,8}/g)]
      .map(match => match[0]).slice(0, 12);
    const components = [...String(componentStack).slice(0, 8000).matchAll(/^\s*(?:at|in) ([A-Za-z_$][\w$]{0,79})(?=\s|$)/gm)]
      .map(match => match[1]).slice(0, 20);
    const scripts = Array.from(document.scripts).map(script => script.src).join('\n');
    const release = (scripts.match(bundlePattern) || []).find(name => name.startsWith('main.')) || '';
    const area = window.location.pathname.split('/')[1].toLowerCase();
    const diagnostic = chunk && errorCode(String(error?.message || '')) === 'chunk_load' ? {
      chunk_asset: typeof chunk.asset === 'string' && chunkAsset.test(chunk.asset) ? chunk.asset : '',
      chunk_status: Number.isInteger(chunk.status) && chunk.status >= 100 && chunk.status <= 599 ? chunk.status : 0,
      chunk_cache: ['network', 'cache'].includes(chunk.cache) ? chunk.cache : 'unknown',
      recovery_action: recoveryActions.has(chunk.action) ? chunk.action : '',
      recovery_reference: /^EB-[A-Z0-9]{6,16}-[A-Z0-9]{1,8}$/.test(chunk.reference || '') ? chunk.reference : '',
      recovery_from_release: /^(?:main|[0-9]{1,10})\.[a-f0-9]{8,64}(?:\.chunk)?\.js$/.test(chunk.fromRelease || '') ? chunk.fromRelease : '',
    } : {};
    const body = JSON.stringify({ ...diagnostic, reference, error_kind: kinds.has(error?.name) ? error.name : 'Error',
      error_code: errorCode(String(error?.message || '').slice(0, 2000)),
      page: pages.has(area) ? area : 'other', frames, components, release });
    sent.add(reference);
    // Fire and forget, including before chunk recovery reload. Never use the
    // authenticated API client: this must also work on public booking screens.
    Promise.resolve(fetch(`${base.replace(/\/$/, '')}/diagnostics/frontend-errors`, {
      method: 'POST', credentials: 'omit', keepalive: true,
      headers: { 'Content-Type': 'application/json' }, body,
    })).catch(() => {});
  } catch (_) {
    // Diagnostics must never replace the original error or break recovery.
  }
};
